import result from "@/gql/graphql"
import { relayStylePagination } from "@apollo/client/utilities"
import { InMemoryCache } from "@apollo/experimental-nextjs-app-support"

const cacheKeys = {
  // TODO: Switch to @connection instead?
  // https://www.apollographql.com/docs/react/pagination/key-args/#the-connection-directive
  individualCreative: [
    "first",
    "excludeMe",
    "name",
    "inMyContacts",
    "orderBy",
    "professions",
    "location",
    "skills",
    "creativeWorkTypes",
    "filters",
    "order",
  ],
}

export const cache = new InMemoryCache({
  possibleTypes: result.possibleTypes,
  addTypename: true,
  typePolicies: {
    Query: {
      fields: {
        allUsers: relayStylePagination(["first"]),
        allLanguages: relayStylePagination(),
        allGenders: relayStylePagination(),
        allRaces: relayStylePagination(),
        allEthnicities: relayStylePagination(),
        allRaceEthnicities: relayStylePagination(),
        allSocialApps: relayStylePagination(),
        globalSearch: relayStylePagination(["first"]),
        search: relayStylePagination(["first"]),
        entitySearch: relayStylePagination(["first", "query", "tags"]),
        allEditorialLists: relayStylePagination(["first", "inPrimarySection", "inSecondarySection"]),
        allEntities: relayStylePagination(),
        allCreativeProfessions: relayStylePagination(["first", "id", "idIn", "idNotIn", "name", "orderBy"]),
        allCreativeEquipment: relayStylePagination(),
        allCreativeWorkTypes: relayStylePagination(["first", "name", "id", "idNotIn", "idIn", "orderBy"]),
        allCreativeIndustries: relayStylePagination(),
        allSkills: relayStylePagination(["first", "types", "name", "idIn", "idNotIn", "id", "orderBy"]),
        allProductionRoles: relayStylePagination(["first", "types", "name", "idIn", "idNotIn", "id"]),
        tagSuggestions: relayStylePagination(),
        allAspectRatios: relayStylePagination(),
        allCreatives: relayStylePagination(["first"]),
        allIndividualCreatives: relayStylePagination(cacheKeys.individualCreative),
        allOrganizations: relayStylePagination(["first", "name"]),
        allProfessionalAssociations: relayStylePagination(["first", "name"]),
        allJobPostingCommitments: relayStylePagination(),
        allCreativeApplications: relayStylePagination(["first"]),
        allJobPostings: relayStylePagination([
          "first",
          "status",
          "title",
          "ownerKind",
          "positions",
          "creativeWorkTypes",
          "skills",
          "startDate",
          "startDateRangeBefore",
          "startDateRangeAfter",
          "endDate",
          "endDateRangeBefore",
          "endDateRangeAfter",
          "compensationMin",
          "compensationMax",
          "payType",
          "payRate",
          "employmentType",
          "locationType",
          "location",
        ]),
      },
    },

    CreativeWorkPortfolioProject: {
      fields: {
        thumbnailContentPosition: {
          read(_, { readField }) {
            const x = parseFloat(readField("thumbnailFocalPointX") || "0.5")
            const y = parseFloat(readField("thumbnailFocalPointY") || "0.5")

            return {
              left: `${x * 100}%`,
              top: `${y * 100}%`,
            }
          },
        },
        /* thumbnailUrl: {
          read(existing, { readField, cache }) {
            if (existing) {
              return existing
            }
            const files = readField('files')
            for (const edge of files?.edges ?? []) {
              const file = edge?.node
              const fileId = cache.identify(file)
              const data = cache.readFragment<MediaFilePreviewCardFragment>({
                id: fileId,
                fragment: MediaFilePreviewCardFragmentDoc,
                fragmentName: 'MediaFilePreviewCard',
              })
              if (file?.thumbnailUrl) {
                return file.thumbnailUrl
              }
            }
            return null
          },
        }, */

        creatorRoles: relayStylePagination(),
        associations: relayStylePagination(),
        contributors: relayStylePagination(),
        files: relayStylePagination(),
      },
    },

    JobPosting: {
      fields: {
        skills: relayStylePagination(["first"]),
        // todo: filter creative__name__icontains, shortlisted, archived, messaged
        applications: relayStylePagination([
          "first",
          "applicantName",
          "unviewed",
          "shortlisted",
          "hidden",
          "archived",
          "status",
          "unreviewed",
          "filters",
        ]),
        invitations: relayStylePagination(["first"]),
        chats: relayStylePagination(["first"]),
      },
    },

    JobInteraction: {},
    JobApplication: {},
    JobInvitation: {},

    Creative: {
      fields: {
        jobPostingInteractions: relayStylePagination(["first", "archived"]),
        creativeWorkPortfolioProjects: relayStylePagination(["first", "status", "randomize", "idNotIn", "orderBy"]),
        jobPostings: relayStylePagination(["first", "status", "title"]),
      },
    },

    ProfessionCategory: {
      fields: {
        individualCreatives: relayStylePagination(cacheKeys.individualCreative),
      },
    },

    CreativeWorkCategory: {
      fields: {
        individualCreatives: relayStylePagination(cacheKeys.individualCreative),
      },
    },

    SkillCategory: {
      fields: {
        individualCreatives: relayStylePagination(cacheKeys.individualCreative),
      },
    },

    Client: {
      fields: {
        jobPostings: relayStylePagination(["first", "status", "title"]),
      },
    },

    IndividualClient: {
      fields: {
        creativeWorkTypes: relayStylePagination(["first"]),
        languages: relayStylePagination(["first"]),
      },
    },

    IndividualCreative: {
      fields: {
        professions: relayStylePagination(["first"]),
        languages: relayStylePagination(["first"]),
        industries: relayStylePagination(["first"]),
        skills: relayStylePagination(["first"]),
        equipment: relayStylePagination(["first"]),
        creativeWorkTypes: relayStylePagination(["first", "creatorRoles", "orderBy"]),
        receivedJobInvitations: relayStylePagination(["first", "status", "archived"]),
        creativeWorkPortfolioProjects: relayStylePagination(["first", "status", "randomize", "idNotIn", "orderBy"]),
        representations: relayStylePagination(["first"]),
        professionalAssociations: relayStylePagination(["first"]),
        associationMemberships: relayStylePagination(["first"]),
        receivedReferralRequests: relayStylePagination(["first", "isPending", "filters"]),
        jobApplications: relayStylePagination(["first", "status", "archived", "filters"]),
      },
    },

    IndividualCreativeApplication: {
      fields: {
        professions: relayStylePagination(),
        creativeWorkTypes: relayStylePagination(),
      },
    },

    User: {
      fields: {
        blocking: relayStylePagination(),
        phoneNumbers: relayStylePagination(),
        contacts: relayStylePagination(),
        organizations: relayStylePagination(),
        clientTeamsOwned: relayStylePagination(),
        clientTeams: relayStylePagination(),
        creativeTeamsOwned: relayStylePagination(),
        sentJobInvitations: relayStylePagination(["first", "status", "archived"]),
        jobApplications: relayStylePagination(["first", "status", "filters"]),
        editorialLists: relayStylePagination(["first"]),
        saveLists: relayStylePagination(),
        saveListItems: relayStylePagination(),
        sentReferralRequests: relayStylePagination(),
        suggestedCreatives: relayStylePagination(["first", "inMyContacts", "isConnectedWith", "orderBy"]),
        frequentCollaborators: relayStylePagination(),
        creativeTimelineFeed: relayStylePagination(["first"]),
        aggregatedCreativeTimelineFeed: relayStylePagination(["first"]),
        notificationsFeed: relayStylePagination(["first"]),
        friends: relayStylePagination(["first"]),
        mutualFriends: relayStylePagination(["first"]),
        friendPreviews: relayStylePagination(["first", "type"]),
        connections: relayStylePagination(["first", "type"]),
        sentConnectionRequests: relayStylePagination(["first", "rejected"]),
        receivedConnectionRequests: relayStylePagination(["first", "rejected"]),
        emailAddresses: relayStylePagination(),
        socialAccounts: relayStylePagination(["provider"]),
      },
    },
  },
})
